/**
 * The main orb button — the primary interaction point.
 *
 * Visual states driven by the FSM's derived DisplayState:
 * - idle:      Dark, dim pulsing glow. Tap → activate/listen.
 * - listening:  Bright orange glow with pulsing animation. Tap → stop.
 * - thinking:   Orange with rotating animation.
 * - streaming:  Orange with rotating animation (same as thinking visually).
 * - speaking:   Green radial glow with pulsing animation. Tap → interrupt.
 * - error:      Red glow (momentary).
 *
 * The orb is the central UI element — large, touchable, always visible.
 * Accessibility: role="button", keyboard Enter support, aria-label.
 */

import { useCallback } from 'react';
import type { DisplayState } from '../fsm/machine';
import styles from './Orb.module.css';

interface OrbProps {
  /** Current visual state derived from the FSM */
  displayState: DisplayState;
  /** Status text shown below the orb */
  statusText: string;
  /** Called when the orb is tapped/clicked */
  onTap: () => void;
  /** Optional: audio level (0-1) for dynamic scaling during listening/speaking */
  audioLevel?: number;
}

/**
 * Get the appropriate icon for the current display state.
 */
function getOrbIcon(state: DisplayState): string {
  switch (state) {
    case 'speaking':
      return '🔊';
    case 'thinking':
    case 'streaming':
      return '💭';
    case 'accumulating':
      return '✍️';
    case 'listening':
      return '🎤';
    case 'idle':
    default:
      return '🎤';
  }
}

export function Orb({
  displayState,
  statusText,
  onTap,
  audioLevel = 0,
}: OrbProps) {
  /** Dynamic scale based on audio level for listening/speaking states */
  const dynamicStyle =
    (displayState === 'listening' || displayState === 'speaking') &&
    audioLevel > 0
      ? { transform: `scale(${1 + audioLevel * 0.15})` }
      : undefined;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onTap();
      }
    },
    [onTap],
  );

  return (
    <div className={styles.container}>
      <div
        className={`${styles.orb} ${styles[displayState]}`}
        onClick={onTap}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={statusText}
        style={dynamicStyle}
      >
        <span className={styles.icon}>{getOrbIcon(displayState)}</span>
      </div>
      <div className={styles.status}>{statusText}</div>
    </div>
  );
}
